import LocalizationService from "services/localizationService";
import { computed, observable } from "mobx";
import Enum from "models/enum";
import EnumService from "services/enumService";
import ChargeTypesService from "services/chargeTypesService";
import { bound } from "@frui.ts/helpers";
import ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { interfaces } from "inversify";
import FuelTypeResponse from "entities/fuelTypeResponse";
import ChargeOrderService from "services/chargeOrderService";
import TimeService from "services/timeService";
import AppRouter from "services/appRouter";
import LicenseViewModel from "../steps/licenseViewModel";
import ChargeTypeViewModel from "../steps/chargeTypeViewModel";
import FuelTypeViewModel from "../steps/fuelTypeViewModel";
import ContactViewModel from "../steps/contactViewModel";
import VehicleRepository from "repositories/vehicleRepository";
import ChargeOrderInstructionsResponse from "entities/chargeOrderInstructionsResponse";

export type EditStep = "licensePlate" | "chargeType" | "fuelType" | "validitySince" | "contact";

export default class OrderRecapitulationViewModel extends ScreenBase {
  @observable isNavigating = false;

  constructor(
    public chargeOrderService: ChargeOrderService,
    public timeService: TimeService,
    public localizationService: LocalizationService,
    private enumService: EnumService,
    private chargeTypesService: ChargeTypesService,
    private router: AppRouter,
    private vehicleRepository: VehicleRepository
  ) {
    super();

    this.initCountriesEnum();
  }

  private initCountriesEnum() {
    this.enumService.values("countries");
  }

  protected async onActivate() {
    return super.onActivate();
  }

  @computed
  get isDifferentTimeZone() {
    return this.timeService.isFetched && !this.timeService.isServerTimeSame;
  }

  @computed
  get serverTime() {
    return this.timeService.currentServerTime;
  }

  @computed
  get chargeOrderItem() {
    return this.chargeOrderService.chargeOrderItem;
  }

  @computed
  get chargeOrder() {
    return this.chargeOrderService.chargeOrder;
  }

  @computed
  get chargeType() {
    return this.chargeTypesService.findChargeType(this.chargeOrderItem._chargeTypeId);
  }

  @computed
  get emptyState() {
    return this.localizationService.translateGeneral("eshop.order.summary.empty");
  }

  @computed
  get afterValidityStart() {
    return this.chargeOrderItem.validity.validSinceDate && this.chargeOrderItem.validity.validSinceDate < this.serverTime;
  }

  @computed
  get vehicleSummaryInfo() {
    void this.enumService.isEnumLoaded("countries");
    const { countryId, licensePlate } = this.chargeOrderItem;
    const country = this.enumService.values("countries").find((country: Enum) => country.id === countryId);

    return countryId && licensePlate && country && country.code ? `${country.code} – ${licensePlate}` : this.emptyState;
  }

  @computed
  get validitySinceInfo() {
    if (this.chargeOrderItem.validity.validSinceDate !== undefined) {
      return this.chargeOrderItem.validity.validSinceDate;
    } else {
      return null;
    }
  }

  @computed
  get validityUntilInfo() {
    const validUntil = this.chargeOrderItem._validUntil ?? this.chargeOrderItem.validity._validUntilDate;
    return validUntil ?? null;
  }

  @computed
  get emailInfo() {
    const emailAddress = this.chargeOrder.emailAddress;

    return emailAddress ? emailAddress : this.emptyState;
  }

  @computed
  get fuelTypeInfo() {
    const fuelTypes = this.enumService.values("fuel_types") as FuelTypeResponse[];
    const fuelType = fuelTypes.find(fuelType => fuelType.id === this.chargeOrderItem._fuelTypeId)?.fuelType;

    return fuelType ? this.localizationService.translateAttribute("fuelType", fuelType) : this.emptyState;
  }

  @computed
  get markSummaryInfo() {
    if (!this.chargeType) {
      return this.emptyState;
    }
    return `${this.localizationService.translateAttribute("chargeType", this.chargeType.validFor)} `;
  }

  @computed
  get priceSummaryInfo() {
    const price = this.chargeOrderItem._priceListItems.find(
      item => item.chargeTypeId === this.chargeOrderItem._chargeTypeId
    )?.price;

    if (!price) {
      return this.emptyState;
    }

    return this.localizationService.formatPrice(price);
  }

  @computed
  get price() {
    const price = this.chargeOrderItem._priceListItems.find(
      item => item.chargeTypeId === this.chargeOrderItem._chargeTypeId
    )?.price;

    if (!price) {
      return undefined;
    }

    return price;
  }

  @bound
  translate(code: string) {
    return this.localizationService.translateGeneral(`eshop.order.summary.${code}`);
  }

  @computed
  get hasFuelTypeLimitation() {
    return !!this.chargeOrderItem._fuelTypeLimitation?.allowedFuelTypeId;
  }

  @computed
  get isSmartRedirect() {
    return true;
  }

  @computed
  get canEditFuelType() {
    return !this.chargeOrderService.smartRedirectFuelTypeLocked && !this.hasFuelTypeLimitation;
  }

  @bound
  async navigateToStep(step: EditStep) {
    switch (step) {
      case "licensePlate":
        this.router.navigate(LicenseViewModel);
        break;
      case "chargeType":
      case "validitySince":
        this.router.navigate(ChargeTypeViewModel);
        break;
      case "fuelType":
        await this.navigateToFuelType();
        break;
      case "contact":
        this.router.navigate(ContactViewModel);
        break;
    }
  }

  @bound
  private async navigateToFuelType() {
    const chargeOrderItem = this.chargeOrderService.chargeOrderItem;
    const { countryId, licensePlate } = chargeOrderItem;

    if (countryId && licensePlate) {
      try {
        this.isNavigating = true;
        const response = await this.vehicleRepository.getChargeOrderInstruction(countryId, licensePlate);
        if (response instanceof ChargeOrderInstructionsResponse && response.fuelTypeLimitation) {
          chargeOrderItem._fuelTypeLimitation = response.fuelTypeLimitation;
        } else {
          chargeOrderItem._fuelTypeLimitation = undefined;
        }
        this.chargeOrderService.persistChargeOrder();
      } catch (e) {
        // Silence catch - continue navigation even on error
      } finally {
        this.isNavigating = false;
      }
    }

    this.router.navigate(FuelTypeViewModel);
  }

  static Factory({ container }: interfaces.Context) {
    return () => {
      return new OrderRecapitulationViewModel(
        container.get(ChargeOrderService),
        container.get(TimeService),
        container.get(LocalizationService),
        container.get(EnumService),
        container.get(ChargeTypesService),
        container.get(AppRouter),
        container.get(VehicleRepository)
      );
    };
  }
}
